import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchPastBishops } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Calendar, Crown } from 'lucide-react';

interface PastBishop {
  id: string;
  title: string;
  name: string;
  tenure: string;
  photo: string;
  qualifications: string;
  enthronement_date: number;
  endYear: number;
  bio: string;
  achievements?: string[];
}

const PastBishops = () => {
  const [pastBishops, setPastBishops] = useState<PastBishop[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadPastBishops = async () => {
      try {
        const response = await fetchPastBishops();
        setPastBishops(response.data.bishops);
      } catch (error) {
        console.error('Error fetching past bishops:', error);
        toast({
          title: "Note",
          description: "Using sample data. API connection will be established for live data.",
          variant: "default"
        });
      } finally {
        setLoading(false);
      }
    };

    loadPastBishops();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading past bishops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Past Bishops
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Honoring the legacy of those who served before us with dedication and wisdom
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10">
          {pastBishops
            .sort((a, b) => b.enthronement_date - a.enthronement_date)
            .map((bishop) => (
              <Card
                key={bishop.id}
                className="overflow-hidden shadow-lg rounded-xl border-none hover:shadow-xl transition-all"
              >
                <img
                  src={bishop.photo}
                  alt={bishop.name}
                  className="w-full h-64 object-cover"
                />
                <CardContent className="p-6 space-y-4">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Crown className="h-6 w-6 text-purple-600" />
                    {bishop.title} {bishop.name}
                  </h2>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    {bishop.enthronement_date} • {bishop.qualifications}
                  </p>
                  <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">
                    {bishop.bio}
                  </p>
                  {bishop.achievements?.length ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mt-4">Key Achievements</h3>
                      <ul className="list-disc list-inside text-gray-700 text-sm mt-2 space-y-1">
                        {bishop.achievements.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
        </div>

        {pastBishops.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No past bishops information available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastBishops;
